import React, { useRef, useState } from "react";
import { Chip } from "@sscs/ui";
import styles from "./emailInput.module.scss";
import {
  getEditValue,
  shouldFlushEmailForEventKey,
  shouldUndoForEventKey,
} from "./utils";

type EditableChipProps = {
  label: string;
  onEdit: (newValue: string) => void;
  className?: string | undefined;
  onDelete: () => void;
};

export const EditableChip = ({
  label,
  onEdit,
  onDelete,
  className,
}: EditableChipProps) => {
  const [editing, setEditing] = useState(false);

  const chipWidthRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commitEdit = (editValue: string | undefined) => {
    setEditing(false);
    if (!editValue) {
      onDelete();
    } else if (editValue !== label) {
      onEdit(editValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (shouldFlushEmailForEventKey(event.key)) {
      event.preventDefault();
      commitEdit(getEditValue(inputRef?.current?.value));
    } else if (shouldUndoForEventKey(event.key)) {
      setEditing(false);
    }
  };

  // needed to prevent the Autocomplete from getting the event too and blurring the input (and thus committing changes)
  const handleMouseEvent = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => {
    event.stopPropagation();
  };

  if (editing) {
    return (
      <div
        className={styles["editable-email-field"]}
        style={{
          width: chipWidthRef.current
            ? `${chipWidthRef.current.valueOf()}px`
            : `${label.length}ch`,
        }}
      >
        <input
          ref={inputRef}
          autoFocus
          defaultValue={label}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseEvent}
          onClick={handleMouseEvent}
          onBlur={(event) => {
            // Clicking the form's submit must not race the draft back into
            // the chip list — the submit validates the chips as they were,
            // and the abandoned draft is discarded.
            if (
              event.relatedTarget instanceof HTMLElement &&
              event.relatedTarget.closest(
                'button[type="submit"], input[type="submit"]',
              )
            ) {
              setEditing(false);
              return;
            }

            commitEdit(getEditValue(inputRef?.current?.value));
          }}
        />
      </div>
    );
  }

  return (
    <Chip
      label={label}
      onClick={(event) => {
        event.stopPropagation();
        chipWidthRef.current = event.currentTarget.offsetWidth;
        setEditing(true);
      }}
      className={className}
      onDelete={onDelete}
    />
  );
};
